type OmitAttributes<K extends string> =
  | Set<K>
  | readonly (K | false | null | undefined)[];

type OmitReturn<T, K extends string> = Omit<T, K> &
  Partial<Pick<T, K & keyof T>>;

/**
 * Remove specified keys from an object
 *
 * Note: it is impossible to exactly determine which keys were passed
 * to the omit to safely remove them from the return type - e.g. returning Omit<T, K>
 * would result to empty object if we can't infer K to more exact type than 'string'.
 *
 * Therefore the return type is described as original T with keys covered by K partial,
 * because those keys might or might not be removed, while keys not covered by K
 * definitely stayed in place.
 */
export const omit: {
  <T, K extends string>(
    attributes: OmitAttributes<K>,
    obj: T,
  ): OmitReturn<T, K>;
  <T, K extends string>(
    attributes: OmitAttributes<K>,
  ): (obj: T) => OmitReturn<T, K>;
  <K extends string>(
    attributes: OmitAttributes<K>,
  ): <T>(obj: T) => OmitReturn<T, K>;
} = <T, K extends string>(attributes: OmitAttributes<K>, obj?: T): any => {
  if (!obj) {
    return (obj2: T) => omit(attributes, obj2);
  }
  const keys: Set<K> =
    attributes instanceof Set
      ? attributes
      : new Set(
          attributes.filter(
            (x): x is K => x !== false && x !== null && x !== undefined,
          ),
        );
  if (keys.size === 0) {
    // nothing to omit, avoid unnecessary clone
    return obj;
  }
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (!keys.has(key as K)) {
        (acc as any)[key] = value;
      }
      return acc;
    },
    {} as Omit<T, K>,
  );
};
